import { Story } from "@/types/cmsTypes";
import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import {
  createLureoPinIcon,
  LUREO_PIN_ANCHOR_X,
  LUREO_PIN_ANCHOR_Y,
  LUREO_PIN_DEFAULT_HEIGHT,
  LUREO_PIN_DEFAULT_WIDTH,
  SVG_PILL_HEIGHT,
  SVG_PILL_RADIUS,
  SVG_PIN_CENTER_X,
  SVG_TEXT_OFFSET,
  SVG_TEXT_Y_BASE,
  SVG_VIEWBOX_HEIGHT,
  SVG_VIEWBOX_WIDTH
} from '@/utils/mapMarkerSvg';
import { runtimeEnv } from '@/lib/runtimeEnv';
import { Card } from '@/components/ui/card';
import { themeColour, themeHex, useThemeVersion } from '@/modules/theme';

interface ProjectMapProps {
  stories: Story[];
  onStorySelect?: (story: Story) => void;
  selectedStoryId?: string;
  allowMapClick?: boolean;
  onMapClick?: (lat: number, lng: number) => void;
  centerLocation?: { lat: number; lng: number };
  hideNumbers?: boolean;
  showNoLocationMessage?: boolean;
  isStoryEditPage?: boolean; // New prop to distinguish story edit vs project page
  onSaveMapImage?: (imageUrl: string | null) => void;
  projectId?: string;
  userId?: string;
  heightClass?: string; // Override the default h-96 container height (e.g. for compact embeds)
  hideLegend?: boolean; // Hide the "Story Status" legend overlay
}


/** Google's silver basemap in the theme's colours: land its page, labels its text, roads and places its lines, parks and water its own. */
const basemapStyles = (): google.maps.MapTypeStyle[] => [
  {
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-map-land') }]
  },
  {
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-label') }]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: themeHex('lureo-map-land') }]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-parcel-label') }]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-map-land') }]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-label') }]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-map-park') }]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-water-label') }]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-surface') }]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-label') }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-map-line') }]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-label') }]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-water-label') }]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-map-park') }]
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-map-land') }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: themeHex('lureo-map-water') }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: themeHex('lureo-map-water-label') }]
  }
        ];

export interface ProjectMapRef {
  saveMapImage: () => Promise<void>;
  saveProjectMapScreenshot: (allStories: any[]) => Promise<void>;
}

export const ProjectMap = forwardRef<ProjectMapRef, ProjectMapProps>(({ stories, onStorySelect, selectedStoryId, allowMapClick, onMapClick, centerLocation, hideNumbers, showNoLocationMessage = false, isStoryEditPage = false, onSaveMapImage, projectId, userId, heightClass = 'h-96', hideLegend = false }, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const services = useServices();
  // The markers are images that cannot read CSS variables: rebuilt in the theme's colours when it changes
  const themeVersion = useThemeVersion();
  
  // Map configuration constants - single source of truth
  const STORY_EDIT_ZOOM = 16; // Close zoom for story editing
  const PROJECT_VIEW_ZOOM = 16; // Broader zoom for project overview
  const MAP_SNAPSHOT_SIZE = 640; // Better resolution with scale=2 = 1024x1024 effective
  const SNAPSHOT_MARKER_SCALE = 1.5; // Make markers larger in saved images for better visibility

  const zoomLevel = isStoryEditPage ? STORY_EDIT_ZOOM : PROJECT_VIEW_ZOOM;

  // The map and its listeners are built once, from the props of the first render
  const initPropsRef = useRef({ stories, allowMapClick, onMapClick, centerLocation, isStoryEditPage, zoomLevel });

  // Map settings that get passed to the service
  const getMapSettings = useCallback(() => {
    const validStories = stories.filter(s => s.locationInfo?.coordinates?.lat && s.locationInfo?.coordinates?.lng);
    if (validStories.length === 0) return null;

    const centerLat = validStories.reduce((sum, s) => sum + s.locationInfo!.coordinates.lat, 0) / validStories.length;
    const centerLng = validStories.reduce((sum, s) => sum + s.locationInfo!.coordinates.lng, 0) / validStories.length;

    return {
      center: { lat: centerLat, lng: centerLng },
      zoom: zoomLevel,
      markerAnchor: {
        x: LUREO_PIN_ANCHOR_X * SNAPSHOT_MARKER_SCALE,
        y: LUREO_PIN_ANCHOR_Y * SNAPSHOT_MARKER_SCALE
      },
      markerSize: {
        width: LUREO_PIN_DEFAULT_WIDTH * SNAPSHOT_MARKER_SCALE,
        height: LUREO_PIN_DEFAULT_HEIGHT * SNAPSHOT_MARKER_SCALE
      },
      mapSize: { width: MAP_SNAPSHOT_SIZE, height: MAP_SNAPSHOT_SIZE },
      svgViewBox: { width: SVG_VIEWBOX_WIDTH, height: SVG_VIEWBOX_HEIGHT },
      svgPinCenter: { x: SVG_PIN_CENTER_X },
      svgTextSettings: {
        yBase: SVG_TEXT_Y_BASE,
        offset: SVG_TEXT_OFFSET,
        pillHeight: SVG_PILL_HEIGHT,
        pillRadius: SVG_PILL_RADIUS
      }
    };
  }, [stories, zoomLevel]);

  // Save map image function
  const handleSaveMapImage = useCallback(async () => {
    if (!projectId || !userId || !onSaveMapImage) return;

    const mapSettings = getMapSettings();
    if (!mapSettings) {
      onSaveMapImage(null);
      return;
    }

    try {
      const serviceStories = stories
        .filter((s) => s.locationInfo?.coordinates?.lat && s.locationInfo?.coordinates?.lng)
        .map((s) => ({
          id: s.id,
          title: s.title,
          order: s.order,
          latitude: s.locationInfo!.coordinates.lat,
          longitude: s.locationInfo!.coordinates.lng
        }));

      const imageUrl = await services.mapSnapshot.saveProjectMapSnapshot({
        projectInstanceId: projectId,
        stories: serviceStories,
        userId: userId,
        mapSettings
      });

      onSaveMapImage(imageUrl);
    } catch (error) {
      console.error('Error saving map image:', error);
      onSaveMapImage(null);
    }
  }, [projectId, userId, onSaveMapImage, getMapSettings, stories, services.mapSnapshot]);

  // Save project map screenshot for any set of stories (used by StoryEdit)
  const saveProjectMapScreenshot = useCallback(async (allStories: any[]) => {
    if (!projectId || !userId) return;

    // Filter stories that have location data
    const storiesWithLocation = allStories.filter(
      s => s.locationInfo?.coordinates?.lat && s.locationInfo?.coordinates?.lng
    );

    if (storiesWithLocation.length === 0) {
      console.log('No stories with location found, skipping map screenshot');
      return;
    }

    // Calculate map settings using the same logic as live map
    const centerLat = storiesWithLocation.reduce((sum, s) => sum + s.locationInfo!.coordinates.lat, 0) / storiesWithLocation.length;
    const centerLng = storiesWithLocation.reduce((sum, s) => sum + s.locationInfo!.coordinates.lng, 0) / storiesWithLocation.length;

    // Use same bounds and zoom calculation as live map
    const lats = storiesWithLocation.map(s => s.locationInfo!.coordinates.lat);
    const lngs = storiesWithLocation.map(s => s.locationInfo!.coordinates.lng);
    const padding = 0.003;
    const bounds = {
      north: Math.max(...lats) + padding,
      south: Math.min(...lats) - padding,
      east: Math.max(...lngs) + padding,
      west: Math.min(...lngs) - padding
    };

    const mapSettings = {
      center: { lat: centerLat, lng: centerLng },
      zoom: zoomLevel,
      bounds: bounds,
      markerAnchor: {
        x: LUREO_PIN_ANCHOR_X * SNAPSHOT_MARKER_SCALE,
        y: LUREO_PIN_ANCHOR_Y * SNAPSHOT_MARKER_SCALE
      },
      markerSize: {
        width: LUREO_PIN_DEFAULT_WIDTH * SNAPSHOT_MARKER_SCALE,
        height: LUREO_PIN_DEFAULT_HEIGHT * SNAPSHOT_MARKER_SCALE
      },
      mapSize: { width: MAP_SNAPSHOT_SIZE, height: MAP_SNAPSHOT_SIZE },
      svgViewBox: { width: SVG_VIEWBOX_WIDTH, height: SVG_VIEWBOX_HEIGHT },
      svgPinCenter: { x: SVG_PIN_CENTER_X },
      svgTextSettings: {
        yBase: SVG_TEXT_Y_BASE,
        offset: SVG_TEXT_OFFSET,
        pillHeight: SVG_PILL_HEIGHT,
        pillRadius: SVG_PILL_RADIUS
      }
    };

    // Convert to service format
    const serviceStories = storiesWithLocation.map((s) => ({
      id: s.id,
      title: s.title,
      order: s.order,
      latitude: s.locationInfo!.coordinates.lat,
      longitude: s.locationInfo!.coordinates.lng
    }));

    await services.mapSnapshot.saveProjectMapSnapshot({
      projectInstanceId: projectId,
      stories: serviceStories,
      userId: userId,
      mapSettings
    });

    console.log('Project map screenshot updated successfully');
  }, [projectId, userId, services.mapSnapshot, zoomLevel]);

  // Expose save functions for external use via ref
  useImperativeHandle(ref, () => ({
    saveMapImage: handleSaveMapImage,
    saveProjectMapScreenshot: saveProjectMapScreenshot
  }), [handleSaveMapImage, saveProjectMapScreenshot]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || map) return;
    const { stories, allowMapClick, onMapClick, centerLocation, isStoryEditPage, zoomLevel } = initPropsRef.current;

    const initMap = () => {
      if (typeof google === 'undefined' || !google.maps) return;

      // Calculate center and bounds
      const validStories = stories.filter(story => {
        const hasLocationInfo = story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng;
        const hasDirectCoordinates = (story as any).coordinates?.lat && (story as any).coordinates?.lng;
        return hasLocationInfo || hasDirectCoordinates;
      });

      let center = { lat: 53.349805, lng: -6.26031 }; // Default to Dublin
      const bounds = new google.maps.LatLngBounds();

      if (validStories.length > 0) {
        // Calculate center
        const totalLat = validStories.reduce((sum, story) => {
          const lat = story.locationInfo?.coordinates?.lat || (story as any).coordinates?.lat;
          return sum + lat;
        }, 0);
        const totalLng = validStories.reduce((sum, story) => {
          const lng = story.locationInfo?.coordinates?.lng || (story as any).coordinates?.lng;
          return sum + lng;
        }, 0);
        
        center = { 
          lat: totalLat / validStories.length, 
          lng: totalLng / validStories.length 
        };

        // Add all story locations to bounds with padding
        validStories.forEach(story => {
          const position = story.locationInfo?.coordinates?.lat 
            ? {
                lat: story.locationInfo!.coordinates.lat,
                lng: story.locationInfo!.coordinates.lng
              }
            : {
                lat: (story as any).coordinates.lat,
                lng: (story as any).coordinates.lng
              };
          
          // Extend bounds with smaller padding for closer zoom
          const padding = 0.003; // Roughly 300m at most latitudes - closer view
          bounds.extend({
            lat: position.lat + padding,
            lng: position.lng + padding
          });
          bounds.extend({
            lat: position.lat - padding,
            lng: position.lng - padding
          });
        });
      }

      // Create map
      const mapInstance = new google.maps.Map(mapRef.current!, {
        center,
        zoom: 12,
        disableDefaultUI: false,
        zoomControl: true,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        keyboardShortcuts: true,
        scrollwheel: true,
        gestureHandling: 'greedy',
        styles: basemapStyles(),
      });

      setMap(mapInstance);

      // Add zoom listener to force our zoom level for single markers
      mapInstance.addListener('zoom_changed', () => {
        const validStories = stories.filter(story => {
          const hasLocationInfo = story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng;
          const hasDirectCoordinates = (story as any).coordinates?.lat && (story as any).coordinates?.lng;
          return hasLocationInfo || hasDirectCoordinates;
        });

        if (validStories.length === 1 && !centerLocation) {
          const targetZoom = zoomLevel;
          const currentZoom = mapInstance.getZoom();
          console.log(`Zoom changed to: ${currentZoom}, forcing to: ${targetZoom} (${isStoryEditPage ? 'Story Edit' : 'Project View'})`);
          if (currentZoom !== targetZoom) {
            setTimeout(() => {
              mapInstance.setZoom(targetZoom);
            }, 50);
          }
        }
      });

      // Add click listener to map if allowMapClick is true
      if (allowMapClick && onMapClick) {
        mapInstance.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            onMapClick(event.latLng.lat(), event.latLng.lng());
          }
        });
      }

      // Set initial view based on stories
      console.log(`Valid stories count: ${validStories.length}`);
      if (validStories.length === 1) {
        // For single marker, center and zoom
        const targetZoom = zoomLevel;
        console.log(`Setting initial zoom to ${targetZoom} for single marker (${isStoryEditPage ? 'Story Edit' : 'Project View'})`);
        mapInstance.setCenter(center);
        mapInstance.setZoom(targetZoom);
        
        // Double check zoom was set
        setTimeout(() => {
          const actualZoom = mapInstance.getZoom();
          console.log(`Zoom after initial set: ${actualZoom}, expected: ${targetZoom}`);
        }, 100);
      } else if (validStories.length > 1) {
        // For multiple markers, let fitBounds calculate optimal zoom with minimal padding
        console.log('Setting bounds for multiple markers - letting fitBounds determine optimal zoom');
        mapInstance.fitBounds(bounds, 40); // Reduced padding from 50 to 20 for closer zoom
        
        // After fitBounds, zoom in a bit more for a closer view
        setTimeout(() => {
          const currentZoom = mapInstance.getZoom();
          if (currentZoom && currentZoom < 18) {
            mapInstance.setZoom(Math.min(currentZoom + 1, 18)); // Zoom in one more level
          }
        }, 100);
        
        console.log('Bounds set with closer zoom');
      }
    };

    // Load Google Maps API
    if (typeof google === 'undefined') {
      // Check if script is already being loaded or loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        if (existingScript.getAttribute('data-loaded') === 'true') {
          // Script is already loaded, initialize immediately
          initMap();
        } else {
          // Script is loading, wait for it
          existingScript.addEventListener('load', () => {
            existingScript.setAttribute('data-loaded', 'true');
            initMap();
          });
        }
      } else {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${runtimeEnv.VITE_GOOGLE_API_KEY}&libraries=geometry,places`;
        script.onload = () => {
          script.setAttribute('data-loaded', 'true');
          initMap();
        };
        document.head.appendChild(script);
      }
    } else {
      initMap();
    }
  }, [mapRef, map]);

  // The basemap follows the theme
  useEffect(() => {
    map?.setOptions({ styles: basemapStyles() });
  }, [map, themeVersion]);

  // Create markers when map is ready and stories change
  useEffect(() => {
    if (!map || !stories.length) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    const newMarkers: google.maps.Marker[] = [];

    const validStories = stories.filter(story => {
              const hasLocationInfo = story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng;
              const hasDirectCoordinates = (story as any).coordinates?.lat && (story as any).coordinates?.lng;
              return hasLocationInfo || hasDirectCoordinates;
    });

    validStories.forEach((story) => {
              const position = story.locationInfo?.coordinates?.lat 
                ? {
                    lat: story.locationInfo!.coordinates.lat,
                    lng: story.locationInfo!.coordinates.lng
                  }
                : {
                    lat: (story as any).coordinates.lat,
                    lng: (story as any).coordinates.lng
                  };

      // Use preCalculatedStatus if available, otherwise default to draft
      const status = story.preCalculatedStatus || story.status || 'draft';
      const isSelected = selectedStoryId === story.id;

      // Color based on status - matching StatusBadge colors exactly
      let color;
      switch (status) {
        case 'live':
          color = themeColour('lureo-aqua');
          break;
        case 'archived':
          color = themeColour('lureo-coral');
          break;
        case 'draft':
        default:
          color = themeColour('lureo-map-line'); // darker grey for better visibility
          break;
      }

      // Override for selected state
      if (isSelected) {
        color = themeColour('lureo-emphasis');
      }

      const displayText = story.order ? `Story ${story.order}` : 'Story 1';
      const icon = createLureoPinIcon({
        color,
        label: hideNumbers ? undefined : displayText
      });

      const marker = new google.maps.Marker({
        position,
        map,
        icon,
        title: story.title
      });

      // Add click listener to marker
      marker.addListener('click', () => {
        onStorySelect?.(story);
      });

      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;
  }, [map, stories, selectedStoryId, onStorySelect, centerLocation, hideNumbers, themeVersion]);

  // Center map when centerLocation changes
  useEffect(() => {
    if (map && centerLocation) {
      map.setCenter(centerLocation);
      // Only set zoom for single marker scenarios - let fitBounds handle multiple markers
      const validStories = stories.filter(story => {
        const hasLocationInfo = story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng;
        const hasDirectCoordinates = (story as any).coordinates?.lat && (story as any).coordinates?.lng;
        return hasLocationInfo || hasDirectCoordinates;
      });
      if (validStories.length <= 1) {
        map.setZoom(zoomLevel);
      }
    }
  }, [map, centerLocation, stories, zoomLevel]);

  // Aggressively ensure zoom is set correctly
  useEffect(() => {
    if (!map || !stories.length) return;
    
    const validStories = stories.filter(story => {
      const hasLocationInfo = story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng;
      const hasDirectCoordinates = (story as any).coordinates?.lat && (story as any).coordinates?.lng;
      return hasLocationInfo || hasDirectCoordinates;
    });

    // Force zoom only for single markers - let fitBounds handle multiple markers
    if (validStories.length === 1 && !centerLocation) {
      // Create an interval to force zoom every 500ms until it sticks
      let attempts = 0;
      const maxAttempts = 10;
      
      const forceZoom = () => {
        const targetZoom = zoomLevel;
        const currentZoom = map.getZoom();
        console.log(`Attempt ${attempts + 1}: Current zoom: ${currentZoom}, target: ${targetZoom} (${isStoryEditPage ? 'Story Edit' : 'Project View'} - ${validStories.length} stories)`);
        
        if (currentZoom !== targetZoom && attempts < maxAttempts) {
          map.setZoom(targetZoom);
          attempts++;
          setTimeout(forceZoom, 500);
        } else if (attempts >= maxAttempts) {
          console.log('Max zoom attempts reached');
        } else {
          console.log('Zoom successfully set!');
        }
      };
      
      setTimeout(forceZoom, 200);
    }
  }, [map, stories, centerLocation, isStoryEditPage, zoomLevel]);

  // Check if any stories have location data
  const hasStoriesWithLocation = stories.some(story => {
    const hasLocationInfo = story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng;
    const hasDirectCoordinates = (story as any).coordinates?.lat && (story as any).coordinates?.lng;
    return hasLocationInfo || hasDirectCoordinates;
  });

  return (
    <div className={`${heightClass} relative border-lureo-border-width border-lureo-line-strong rounded-lureo-radius-lg overflow-hidden ${allowMapClick ? 'cursor-pointer' : 'cursor-default'}`}>
      <div ref={mapRef} className={`w-full h-full ${allowMapClick ? 'cursor-pointer' : 'cursor-default'}`} />
      
      {/* No Location Message */}
      {showNoLocationMessage && !hasStoriesWithLocation && (
        <div className="absolute inset-0 flex items-center justify-center bg-lureo-neutral-subtle/80 backdrop-blur-lureo-blur-sm">
          <Card className="max-w-sm text-center">
            <div className="text-lureo-16 leading-6 text-lureo-ink-subtle">
              Please set the location of a story
            </div>
          </Card>
        </div>
      )}
      
      {/* Legend */}
      {hasStoriesWithLocation && !hideLegend && (
        <Card padding="sm" className="absolute bottom-4 left-4">
          <div className="text-lureo-12 font-medium text-lureo-ink-subtle mb-2 leading-4">Story Status</div>
          <div className="flex space-x-3">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded-full bg-lureo-aqua-ink" />
              <span className="text-lureo-12 text-lureo-ink-subtle leading-4">Live</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded-full bg-lureo-map-line" />
              <span className="text-lureo-12 text-lureo-ink-subtle leading-4">Draft</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded-full bg-lureo-coral-ink" />
              <span className="text-lureo-12 text-lureo-ink-subtle leading-4">Archived</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
});